import { yupResolver } from '@hookform/resolvers/yup'
import { SubmitHandler, useForm } from 'react-hook-form'
import { addDomainSchema } from '../../schemas/SettingsSchema'
import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../reducers/store'
import { Button } from '@radix-ui/themes'
import { AddDomainInputs } from '../../types/BlocklistTypes'
import { fetchBlocklist, postDomainsToBlocklist, selectBlocklist } from '../../reducers/blocklist'
import toast from 'react-hot-toast'
import { IconCircleMinus } from '@tabler/icons-react'
import { useSelector } from 'react-redux'
import { Input } from '../input/Input'
import { DEFAULT_TOAST_CONFIG } from '../toast/consts'
import { PlusIcon } from '@radix-ui/react-icons'

interface IAddDomainModalProps {
  setShowModal: (show: boolean) => void
  csvDomainsToBlock: AddDomainInputs[]
  setCsvDomainsToBlock: (domains: AddDomainInputs[]) => void
}

function AddDomainModal(props: IAddDomainModalProps) {
  const { setShowModal, csvDomainsToBlock, setCsvDomainsToBlock } = props
  const { blocklist } = useSelector(selectBlocklist)
  const [loading, setLoading] = useState(false)
  const [domainsToBlock, setDomainsToBlock] = useState<AddDomainInputs[]>([])
  const di = useAppDispatch()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AddDomainInputs>({ resolver: yupResolver(addDomainSchema) })

  useEffect(() => {
    if (csvDomainsToBlock.length > 0) {
      setDomainsToBlock(csvDomainsToBlock)
    }
  }, [csvDomainsToBlock])

  const onSubmit: SubmitHandler<AddDomainInputs> = async (domainInfo) => {
    if (
      !domainsToBlock.some((domain) => domain.domain.toLowerCase() === domainInfo.domain.toLowerCase()) &&
      !(blocklist?.domains ?? []).some((blocked) => blocked.domain.toLowerCase() === domainInfo.domain.toLowerCase())
    ) {
      const newDomainValue = { ...domainInfo }
      setDomainsToBlock([...domainsToBlock, newDomainValue])
      reset()
    } else {
      toast.error('Domain already added', DEFAULT_TOAST_CONFIG)
    }
  }

  const removeDomain = (domainToRemove: string) => {
    const newMembers = domainsToBlock.filter((domain) => domain.domain !== domainToRemove)
    setDomainsToBlock(newMembers)
  }

  const uploadDomainsToBlock = async () => {
    setLoading(true)
    const domainsMatchedInWhitelist = await di(postDomainsToBlocklist(domainsToBlock))
    if (!domainsMatchedInWhitelist) {
      toast.error(
        `Failed to add domain${domainsToBlock.length === 1 ? '' : 's'}, please try again`,
        DEFAULT_TOAST_CONFIG
      )
      setLoading(false)
      return
    }
    if (domainsMatchedInWhitelist.length === 0)
      toast.success(`Successfully added domain${domainsToBlock.length === 1 ? '' : 's'}`, DEFAULT_TOAST_CONFIG)
    if (domainsMatchedInWhitelist.length > 0) {
      const toastMessage = formatDomainsMatchedInWhitelist(domainsMatchedInWhitelist.map((domain) => domain.domain))
      toast.error(`Failed to add ${toastMessage} as they are on our global allowlist`, DEFAULT_TOAST_CONFIG)
    }
    setCsvDomainsToBlock([])
    await di(fetchBlocklist())
    reset()
    setShowModal(false)
  }

  const formatDomainsMatchedInWhitelist = (domains: string[]): string => {
    const count = domains.length
    if (count === 1) return domains[0]
    if (count === 2) return domains.join(', ')

    const firstTwoDomains = domains.slice(0, 2).join(', ')
    const remainingCount = count - 2
    return `${firstTwoDomains} + ${remainingCount > 0 ? remainingCount + ' other' + (remainingCount > 1 ? 's' : '') : ''}`
  }

  return (
    <div
      className="bg-black bg-opacity-50 absolute top-0 left-0 w-full h-full z-50"
      onClick={() => setShowModal(false)}
    >
      <div
        className="fixed w-[724px] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-modalBackground rounded-lg p-6 max-h-[70%] overflow-y-auto border dark:border-gray-600"
        onClick={(e) => e.stopPropagation()}
      >
        <>
          <h2 className="text-xl font-bold mb-2">Add Domain</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="block mt-4">
            <div className="flex">
              <div className="mr-2 w-52">
                <label className="text-secondaryText font-semibold text-xs block pb-1">NAME</label>
                <Input
                  type="text"
                  placeholder="Phishing CDN"
                  {...register('name', { required: true })}
                  onChange={undefined}
                  maxLength={50}
                />
                {errors.name && <p className="text-ErrorRed">{errors.name.message}</p>}
              </div>
              <div className="mr-2 w-52">
                <label className="text-secondaryText font-semibold text-xs block pb-1">DOMAIN</label>
                <Input
                  type="text"
                  placeholder="cdn.example.com"
                  {...register('domain', { required: true })}
                  onChange={undefined}
                />
                {errors.domain && <p className="text-ErrorRed">{errors.domain.message}</p>}
              </div>
              <Button
                type="submit"
                className="w-[36px] h-[36px] bg-mainPurple hover:bg-tertiaryPurple active:active:bg-tertiaryPurple rounded-full mt-[19px] ml-2 p-2.5 text-white font-semibold text-sm cursor-pointer"
                value="Add"
              >
                <PlusIcon />
              </Button>
            </div>
          </form>
          <hr className="my-6 border-t" />

          <table className="table-auto w-full my-4 min-h-10">
            <tbody>
              {domainsToBlock.map((item: AddDomainInputs) => (
                <tr key={item.domain}>
                  <td className="px-4 py-2">{item.name}</td>
                  <td className="px-4 py-2">{item.domain}</td>
                  <td className="py-2 text-right w-6">
                    <IconCircleMinus
                      size={24}
                      color={'#1c2024'}
                      className="cursor-pointer ml-auto"
                      onClick={() => removeDomain(item.domain)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <hr className="my-6 border-t" />

          <div className="flex justify-end mt-6">
            <button
              className="px-4 py-2 mr-2 bg-gray-300 text-neutral-12 rounded"
              onClick={() => {
                setCsvDomainsToBlock([])
                setShowModal(false)
              }}
            >
              Cancel
            </button>
            <Button
              className={`h-10 px-4 py-2 bg-mainPurple text-white ${domainsToBlock.length === 0 || loading ? 'opacity-50' : 'cursor-pointer hover:bg-tertiaryPurple active:active:bg-tertiaryPurple'}`}
              onClick={() => {
                uploadDomainsToBlock()
              }}
              disabled={domainsToBlock.length === 0 || loading}
            >
              {!loading ? 'Block Domains' : 'Loading...'}
            </Button>
          </div>
        </>
      </div>
    </div>
  )
}

export default AddDomainModal
